import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, supabase } from '../lib/supabase-client.js';
import { useDiagMode } from '../lib/use-diag-mode.js';
import AppShell from './AppShell.js';
import { TABS } from './tabRegistry.js';
import { useSectionNav } from './useSectionNav.js';
import FinanceModule from '../domains/finance/FinanceModule.jsx';
import DirectoryModule from '../domains/directory/DirectoryModule.jsx';
import DailyEntryModule from '../domains/daily-entry/DailyEntryModule.jsx';
import SchedulerModule from '../domains/scheduler/SchedulerModule.jsx';
import FleetModule from '../domains/fleet/FleetModule.jsx';
import BugTrapModule from '../domains/bug-trap/BugTrapModule.jsx';
import OpsModule from '../domains/ops/OpsModule.jsx';
import ActivityModule from '../domains/activity/ActivityModule.jsx';
import SafetyModule from '../domains/safety/SafetyModule.jsx';
import SettingsModule from '../domains/settings/SettingsModule.jsx';
import TrainingModule from '../domains/training/TrainingModule.jsx';
import ChartCheck from '../domains/finance/ChartCheck.jsx';
import SessionCheck from '../features/session/SessionCheck.js';
import Panel from '../shared/ui/Panel.js';
import SectionHeader from '../shared/ui/SectionHeader.js';
import { C, LAYOUT } from '../shared/design-tokens.js';
import type { Session } from '@supabase/supabase-js';

// ── Constants ────────────────────────────────────────────────────────────────
const MANAGER_SESSION_KEY = 'pwd_manager_session';
const MANAGER_OAUTH_PENDING_KEY = 'pwd_manager_oauth_pending';
const ALLOWED_DOMAIN = '@prestigeworlddrive.com';

// ── localStorage / sessionStorage helpers (match legacy manager-entry.js) ───

function getManagerSessionToken(): string | null {
  try { return window.localStorage.getItem(MANAGER_SESSION_KEY); } catch { return null; }
}
function setManagerSessionToken(token: string): void {
  window.localStorage.setItem(MANAGER_SESSION_KEY, token);
}
function clearManagerSessionToken(): void {
  try { window.localStorage.removeItem(MANAGER_SESSION_KEY); } catch { /* */ }
}
function markManagerOAuthPending(): void {
  try { window.sessionStorage.setItem(MANAGER_OAUTH_PENDING_KEY, '1'); } catch { /* */ }
}
function hasManagerOAuthPending(): boolean {
  try { return window.sessionStorage.getItem(MANAGER_OAUTH_PENDING_KEY) === '1'; } catch { return false; }
}
function clearManagerOAuthPending(): void {
  try { window.sessionStorage.removeItem(MANAGER_OAUTH_PENDING_KEY); } catch { /* */ }
}
async function clearManagerPrivilegedState(signOutSupabase: boolean): Promise<void> {
  clearManagerSessionToken();
  clearManagerOAuthPending();
  if (signOutSupabase) {
    try { await supabase.auth.signOut({ scope: 'local' }); } catch { /* */ }
  }
}

function generateSessionToken(): string {
  const arr = new Uint8Array(32);
  crypto.getRandomValues(arr);
  return Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('');
}

// ── Validation — direct port of legacy manager-entry.js lines 105-137 ───────
// Takes the session AS AN ARGUMENT — never calls getSession() again.

interface Validation {
  valid: boolean;
  reason: string;
  email?: string;
  error?: unknown;
  sessionToken?: string;
}

async function validateManagerPrivilegedSession(session: Session): Promise<Validation> {
  const user = session.user;
  const email = user?.email || '';
  if (!email) return { valid: false, reason: 'no_supabase_session' };
  if (!email.endsWith(ALLOWED_DOMAIN)) return { valid: false, reason: 'invalid_domain', email };

  const localToken = getManagerSessionToken();
  if (!localToken) return { valid: false, reason: 'missing_local_token', email };

  const result = await supabase
    .from('employees')
    .select('manager_session_token')
    .eq('email', email)
    .maybeSingle();

  if (result.error) return { valid: false, reason: 'query_error', email, error: result.error };
  if (!result.data) return { valid: false, reason: 'missing_employee', email };
  if (result.data.manager_session_token !== localToken) return { valid: false, reason: 'token_mismatch', email };

  return { valid: true, reason: 'valid', email, sessionToken: localToken };
}

// ── UI states — mirrors legacy's showState('loading' | 'login' | 'error' | 'auth') ─
type ShellState = 'loading' | 'login' | 'error' | 'ready';

// ═════════════════════════════════════════════════════════════════════════════
// ManagerShell — single auth controller.
//
// Direct port of legacy manager-entry.js init() + handleSession() +
// establishPrivilegedSession(), but rendered as a React component instead of
// DOM state toggling + hard page redirect.
// ═════════════════════════════════════════════════════════════════════════════

export default function ManagerShell() {
  const [activeTab, setActiveTab] = useSectionNav<string>('overview');
  const [shellState, setShellState] = useState<ShellState>('loading');
  const [errorMsg, setErrorMsg] = useState('');
  const [diagEnabled] = useDiagMode();
  const initRan = useRef(false);

  const visibleTabs = useMemo(() => {
    return TABS.filter((tab) => tab.id !== 'session' || diagEnabled);
  }, [diagEnabled]);

  useEffect(() => {
    if (!visibleTabs.some((tab) => tab.id === activeTab)) {
      setActiveTab('overview');
    }
  }, [visibleTabs, activeTab, setActiveTab]);

  // ── establishPrivilegedSession — port of legacy lines 180-227 ───────────
  // Called when we have a Supabase session but no valid local token.
  // Writes a fresh token to DB + localStorage, then sets state to 'ready'.
  const establishPrivilegedSession = useCallback(async (session: Session) => {
    const user = session.user;
    if (!user.email || !user.email.endsWith(ALLOWED_DOMAIN)) {
      await clearManagerPrivilegedState(true);
      setErrorMsg('Access restricted to @prestigeworlddrive.com accounts. You signed in with: ' + (user.email || 'unknown'));
      setShellState('error');
      return;
    }

    const sessionToken = generateSessionToken();

    // DB write — matches legacy .eq('email').select('id').maybeSingle()
    let { data: employee, error: updateError } = await supabase
      .from('employees')
      .update({ manager_session_token: sessionToken })
      .eq('email', user.email)
      .select('id')
      .maybeSingle();

    // Fallback by auth UUID (matches legacy portal-app.jsx behaviour)
    if ((updateError || !employee) && user.id) {
      const fb = await supabase
        .from('employees')
        .update({ manager_session_token: sessionToken })
        .eq('id', user.id)
        .select('id')
        .maybeSingle();
      employee = fb.data;
      updateError = fb.error;
    }

    if (updateError) {
      await clearManagerPrivilegedState(true);
      setErrorMsg(updateError.message);
      setShellState('error');
      return;
    }
    if (!employee) {
      await clearManagerPrivilegedState(true);
      setErrorMsg('No employee record found for your account. Contact Adam to set up your profile.');
      setShellState('error');
      return;
    }

    // Commit to localStorage — equivalent of legacy line 214-215
    setManagerSessionToken(sessionToken);
    clearManagerOAuthPending();

    // Legacy did: window.location.href = 'portal-react.html'
    // We set state instead — the portal modules render below.
    setShellState('ready');
  }, []);

  // ── handleSession — port of legacy lines 229-265 ───────────────────────
  // Single decision function. Called from init AND from onAuthStateChange.
  const handleSession = useCallback(async (session: Session) => {
    const validation = await validateManagerPrivilegedSession(session);

    if (validation.valid) {
      clearManagerOAuthPending();
      setShellState('ready');
      return;
    }

    if (validation.reason === 'query_error') {
      setErrorMsg(String((validation.error as { message?: string })?.message || 'Could not validate manager session.'));
      setShellState('error');
      return;
    }

    if (validation.reason === 'missing_local_token') {
      await establishPrivilegedSession(session);
      return;
    }

    if (validation.reason === 'token_mismatch' && hasManagerOAuthPending()) {
      await establishPrivilegedSession(session);
      return;
    }

    // Everything else: clear and show appropriate state
    await clearManagerPrivilegedState(true);
    if (validation.reason === 'token_mismatch') {
      setErrorMsg('You were signed out because your account was accessed from another device.');
      setShellState('error');
      return;
    }
    setShellState('login');
  }, [establishPrivilegedSession]);

  // ── init — port of legacy lines 271-321 ────────────────────────────────
  // Runs once on mount. Exactly mirrors the legacy IIFE.
  useEffect(() => {
    if (initRan.current) return;
    initRan.current = true;

    let cancelled = false;

    (async () => {
      try {
        // 1. Check URL ?error params (legacy lines 273-286)
        const queryParams = new URLSearchParams(window.location.search);
        const redirectError = queryParams.get('error');
        if (redirectError === 'no_employee') {
          setErrorMsg('No employee record found for your account. Contact Adam to set up your profile.');
          setShellState('error'); return;
        }
        if (redirectError === 'insufficient_role') {
          setErrorMsg('Manager access requires manager, admin, or god_mode role. Contact Adam if you need elevated access.');
          setShellState('error'); return;
        }
        if (redirectError === 'other_device') {
          setErrorMsg('You were signed out because your account was accessed from another device.');
          setShellState('error'); return;
        }

        // 2. Check URL #error params (legacy lines 288-294)
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const urlError = hashParams.get('error_description') || hashParams.get('error');
        if (urlError) {
          clearManagerOAuthPending();
          setErrorMsg(urlError);
          setShellState('error'); return;
        }

        // 3. Get Supabase session (legacy line 296)
        const sessionResult = await supabase.auth.getSession();
        if (cancelled) return;
        if (sessionResult.error) {
          setErrorMsg(sessionResult.error.message);
          setShellState('error'); return;
        }

        // 4. If session → handleSession; else → login (legacy lines 302-307)
        if (sessionResult.data.session) {
          await handleSession(sessionResult.data.session);
        } else {
          clearManagerOAuthPending();
          setShellState('login');
        }
        if (cancelled) return;

        // 5. Register onAuthStateChange (legacy lines 309-317)
        // Same handleSession — single decision function, no separate bootstrap.
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
          async (event, session) => {
            if (cancelled) return;
            if (event === 'SIGNED_IN' && session) {
              await handleSession(session);
            } else if (event === 'SIGNED_OUT') {
              clearManagerOAuthPending();
              clearManagerSessionToken();
              setShellState('login');
            }
          },
        );

        // Cleanup on unmount
        return () => { cancelled = true; subscription.unsubscribe(); };
      } catch (err) {
        if (cancelled) return;
        setErrorMsg(err instanceof Error ? err.message : 'An unexpected error occurred.');
        setShellState('error');
      }
    })();

    return () => { cancelled = true; };
  }, [handleSession]);

  // ── signIn — port of legacy lines 77-92 ────────────────────────────────
  async function handleSignIn() {
    await clearManagerPrivilegedState(true);
    markManagerOAuthPending();
    const result = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin + '/portal-vite/dist/',
        queryParams: { hd: 'prestigeworlddrive.com' },
      },
    });
    if (result.error) {
      clearManagerOAuthPending();
      setErrorMsg(result.error.message);
      setShellState('error');
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────

  if (shellState === 'loading') {
    return (
      <AppShell tabs={visibleTabs} activeTab={activeTab} onTabChange={setActiveTab}>
        <div style={LAYOUT.panel}>
          <div style={{ textAlign: 'center', padding: '2rem 0' }}>
            <div style={{
              width: 32, height: 32,
              border: `3px solid ${C.gold}`, borderTopColor: C.navy,
              borderRadius: '50%', animation: 'spin 0.8s linear infinite',
              margin: '0 auto 1rem',
            }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            <p style={{ color: C.textMuted, fontSize: 14 }}>Checking session…</p>
          </div>
        </div>
      </AppShell>
    );
  }

  if (shellState === 'error') {
    return (
      <AppShell tabs={visibleTabs} activeTab={activeTab} onTabChange={setActiveTab}>
        <div style={LAYOUT.panel}>
          <div style={{ fontSize: 12, textTransform: 'uppercase' as const, letterSpacing: '0.08em', color: C.red, marginBottom: 12 }}>
            Authentication Error
          </div>
          <div style={{
            background: 'rgba(255,107,107,0.1)', border: '1px solid rgba(255,107,107,0.3)',
            borderRadius: 8, padding: 16, fontSize: 14, lineHeight: 1.6, color: C.text, marginBottom: 16,
          }}>
            {errorMsg}
          </div>
          <button
            onClick={() => { setErrorMsg(''); setShellState('login'); }}
            style={{
              padding: '10px 20px', borderRadius: 8, background: C.surface, color: C.text,
              border: `1px solid ${C.borderPanel}`, cursor: 'pointer', fontWeight: 600, fontSize: 14,
            }}
          >
            Try Again
          </button>
        </div>
      </AppShell>
    );
  }

  if (shellState === 'login') {
    return (
      <AppShell tabs={visibleTabs} activeTab={activeTab} onTabChange={setActiveTab}>
        <div style={LAYOUT.panel}>
          <div style={{ fontSize: 12, textTransform: 'uppercase' as const, letterSpacing: '0.08em', color: C.gold, marginBottom: 12 }}>
            Manager Sign In
          </div>
          <p style={{ marginTop: 0, lineHeight: 1.7, color: C.text, fontSize: 14 }}>
            Sign in with your <span style={{ color: C.gold }}>@prestigeworlddrive.com</span> Google account to access the manager portal.
          </p>
          <div style={{ marginTop: 20 }}>
            <button
              onClick={handleSignIn}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 12,
                padding: '12px 24px', borderRadius: 8, background: '#fff', color: '#333',
                border: '2px solid #dadce0', cursor: 'pointer', fontWeight: 500, fontSize: 15,
                transition: 'background 200ms, border-color 200ms',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#f8f9fa'; e.currentTarget.style.borderColor = '#c6c8ca'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#dadce0'; }}
            >
              <GoogleIcon />
              Sign in with Google
            </button>
          </div>
          <p style={{ fontSize: 12, color: C.textMuted, marginTop: 16 }}>
            Use your @prestigeworlddrive.com email address. Contact Adam if you need account access.
          </p>
        </div>
      </AppShell>
    );
  }

  // shellState === 'ready'
  return (
    <AppShell tabs={visibleTabs} activeTab={activeTab} onTabChange={setActiveTab}>
      {activeTab === 'overview' && <OverviewLanding />}
      {activeTab === 'session' && diagEnabled && <SessionDiagnostics />}
      {activeTab === 'finance' && <FinanceModule />}
      {activeTab === 'directory' && <DirectoryModule />}
      {activeTab === 'daily-entry' && <DailyEntryModule />}
      {activeTab === 'scheduler' && <SchedulerModule />}
      {activeTab === 'fleet' && <FleetModule />}
      {activeTab === 'bug-trap' && <BugTrapModule />}
      {activeTab === 'ops' && <OpsModule />}
      {activeTab === 'activity' && <ActivityModule />}
      {activeTab === 'safety' && <SafetyModule />}
      {activeTab === 'settings' && <SettingsModule />}
      {activeTab === 'training' && <TrainingModule />}
      {activeTab === 'charts' && <ChartCheck />}
    </AppShell>
  );
}

// ── Static sub-components ────────────────────────────────────────────────────

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" width={20} height={20} style={{ flexShrink: 0 }}>
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );
}

function OverviewLanding() {
  return (
    <Panel>
      <SectionHeader
        eyebrow="Welcome"
        title="Operations Console"
        description="Pick a section from the sidebar to begin."
      />
      <p className="text-muted text-sm" style={{ margin: 0 }}>
        Work, people, fleet, finance — each surface stays inside its own pane so
        context does not pile up. Drill-in details open as overlays you can close
        with the close button, Esc, or browser back.
      </p>
    </Panel>
  );
}

function SessionDiagnostics() {
  const clientReady = !!supabase;
  const keyType = SUPABASE_PUBLISHABLE_KEY.startsWith('sb_publishable_') ? 'publishable' : 'other';
  const managerToken = getManagerSessionToken();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Panel>
        <SectionHeader
          eyebrow="Diagnostic"
          title="Session bootstrap"
          description="Supabase client + manager session sanity. Hidden unless diag mode is on for this browsing session."
        />
        <div style={{ lineHeight: 1.7, fontSize: 14 }}>
          <div>Supabase URL: <span style={{ color: C.gold }}>{SUPABASE_URL}</span></div>
          <div>Key type: <span style={{ color: C.gold }}>{keyType}</span></div>
          <div>Client initialized: <span style={{ color: clientReady ? C.green : C.red }}>{clientReady ? 'yes' : 'no'}</span></div>
          <div>Manager token in localStorage: <span style={{ color: managerToken ? C.green : C.red }}>{managerToken ? 'yes' : 'no'}</span></div>
        </div>
      </Panel>
      <SessionCheck />
    </div>
  );
}
